// src/components/Login/DoctorLogin.tsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import drIsInLogo from "../../assets/dr-is-in.png";

type DoctorLoginResponse = {
  ok?: boolean;
  access_token?: string;
  token?: string;
  role?: string;
  doctor_id?: number | string;
  doctor?: {
    id?: number;
    doctor_id?: number;
    name?: string;
    email?: string;
  };
  detail?: any;
};

function normalizeBearer(t: string) {
  return (t || "").replace(/^Bearer\s+/i, "").trim();
}

function safeDetailText(detail: any): string {
  if (!detail) return "";
  if (typeof detail === "string") return detail;

  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");

function setBoth(k: string, v: string) {
  try {
    sessionStorage.setItem(k, v);
  } catch {}

  try {
    localStorage.setItem(k, v);
  } catch {}
}

function removeBoth(k: string) {
  try {
    sessionStorage.removeItem(k);
  } catch {}

  try {
    localStorage.removeItem(k);
  } catch {}
}

async function loginDoctorPin(emailOrLogin: string, pin: string): Promise<DoctorLoginResponse> {
  const endpoints = [`${API_BASE}/auth/login_doctor`];

  const payloads = [
    { emailOrLogin, pin },
    { email: emailOrLogin, pin },
    { login: emailOrLogin, pin },
    { login_id: emailOrLogin, pin },
    { username: emailOrLogin, pin },
    { identifier: emailOrLogin, pin },
    { email_or_login_id: emailOrLogin, pin },
    { email: emailOrLogin, password: pin },
    { login_id: emailOrLogin, password: pin },
  ] as any[];

  let lastErr: any = null;

  for (const endpoint of endpoints) {
    for (const bodyObj of payloads) {
      try {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bodyObj),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          const err: any = new Error("Doctor login failed");
          err.status = res.status;
          err.data = data;
          err.endpoint = endpoint;
          err.payload = bodyObj;
          lastErr = err;
          continue;
        }

        return data as DoctorLoginResponse;
      } catch (e: any) {
        lastErr = e;
        continue;
      }
    }
  }

  throw lastErr || new Error("Doctor login failed");
}

export default function DoctorLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (loading) return;

    setLoading(true);

    try {
      const emailOrLogin = email.trim();
      const pinTrim = pin.trim();

      const data = await loginDoctorPin(emailOrLogin, pinTrim);

      const tokRaw = String(data.access_token || data.token || "");
      const tok = normalizeBearer(tokRaw);

      if (!tok) {
        alert("Login failed: no token returned.\n\n" + safeDetailText(data?.detail));
        return;
      }

      ["student_token", "student_access_token"].forEach(removeBoth);

      setBoth("role", "doctor");

      setBoth("doctor_token", tok);
      setBoth("doctor_access_token", tok);

      setBoth("access_token", tok);
      setBoth("token", tok);

      const docId = data?.doctor_id ?? data?.doctor?.doctor_id ?? data?.doctor?.id;

      if (docId != null) {
        setBoth("doctor_id", String(docId));
      }

      const name = data?.doctor?.name || "";

      if (name) {
        setBoth("display_name", name);
      }

      navigate("/doctor-portal", { replace: true });
    } catch (err: any) {
      const status = err?.status;
      const detail = err?.data?.detail ?? err?.data ?? err?.message;
      const endpoint = err?.endpoint ? `Endpoint: ${err.endpoint}\n\n` : "";

      if (status === 401) {
        alert(`Unauthorized (invalid email/login or PIN).\n\n${endpoint}${safeDetailText(detail)}`);
      } else if (status) {
        alert(`Doctor login failed (${status}).\n\n${endpoint}${safeDetailText(detail)}`);
      } else {
        alert(`Doctor login failed.\n\n${safeDetailText(detail)}`);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "rgba(255,255,255,0.96)",
          borderRadius: 16,
          padding: 20,
          boxShadow: "0 18px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
          <img
            src={drIsInLogo}
            alt="Doctor Is In"
            style={{
              width: 70,
              height: 70,
              objectFit: "contain",
            }}
          />
        </div>

        <h2 style={{ textAlign: "center", margin: "6px 0 14px" }}>Advisor Login</h2>

        <form onSubmit={onSubmit}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
            Email (or Login ID)
          </label>

          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="doctor@example.com"
            autoComplete="username"
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.2)",
              marginBottom: 12,
            }}
          />

          <label style={{ display: "block", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>PIN</label>

          <input
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="******"
            autoComplete="current-password"
            type="password"
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.2)",
              marginBottom: 12,
            }}
          />

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 10,
              border: "none",
              fontWeight: 900,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, fontSize: 13 }}>
          <Link to="/">Home</Link>
          <Link to="/doctor">Back</Link>
        </div>

        <div style={{ textAlign: "center", marginTop: 10, fontSize: 12, opacity: 0.75 }}>
          © 2026 Doctor Is In
        </div>
      </div>
    </div>
  );
}